/**
 * /equipment/pressure-control/calibrations/:equipmentId — one test gauge
 * (api#1281, BOP-2): its calibration state today, the current calibration,
 * and the full history, newest first. Append-only.
 *
 * The state is the API's (GET /equipment/:id/calibration), computed against
 * the tenant's today. A gauge with no calibration reads "Never calibrated",
 * not a date.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Plus } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { PcBadge, usePcEquipmentOptions } from '../../components/pressure-control/PcSectionNav';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { usePcComponents, usePcGaugeCalibration } from '../../hooks/usePressureControl';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { isNotFound, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminPc, PC_CAL_FILL, PC_CAL_LABEL, PC_CALIBRATIONS, pcDay, pcDueDaysPhrase, pcPressure } from '../../lib/pressure-control';
import type { PcCalibration } from '../../types/pressure-control';
import { DhEmpty, DhPanel, DhSpec } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { RecordCalibrationDialog } from './PressureControlCalibrationsScreen';

const range = (c: PcCalibration) =>
  `${c.rangeMin !== null ? `${pcPressure(c.rangeMin, null)} – ` : ''}${pcPressure(c.rangeMax, c.pressureUnit)}`;

export default function PressureControlGaugeScreen() {
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const back = useBackToList(PC_CALIBRATIONS);
  const tz = useTenantTimeZone();
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canRecord = canAdminPc(user?.role, elevated);
  const [recording, setRecording] = useState(false);
  const query = usePcGaugeCalibration(equipmentId);
  const names = usePcEquipmentOptions(true);
  const meta = usePcComponents();
  const unit = meta.data?.meta?.pressureUnit ?? null;
  const failure = queryLoadFailure(query);
  const g = query.data ?? null;
  const name = names.data?.find((n) => n.id === equipmentId)?.name ?? null;
  const cur = g?.current ?? null;

  return (
    <div data-testid="pc-gauge-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Test gauge"
        title={name ?? (query.isLoading ? 'Loading…' : 'Gauge')}
        onBack={back}
        backLabel="Back"
        actions={
          canRecord && g ? (
            <button type="button" onClick={() => setRecording(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Record calibration
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading gauge" />
            </div>
          ) : (failure && isNotFound(queryFailureError(query))) || (!failure && !g) ? (
            <DhPanel eyebrow="Test gauge" title="Not found" testId="pc-gauge-not-found">
              <DhEmpty>
                No such equipment on this account.{' '}
                <Link to={PC_CALIBRATIONS} className="font-semibold underline underline-offset-2">Back to the gauges</Link>
              </DhEmpty>
            </DhPanel>
          ) : failure ? (
            <DhPanel eyebrow="Test gauge" title="Calibration" testId="pc-gauge-load-failure">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this gauge's calibration" />
            </DhPanel>
          ) : g ? (
            <>
              <DhPanel eyebrow="Today" title="Calibration" testId="pc-gauge-current">
                <dl className="grid grid-cols-2 gap-4 px-4 py-4 md:grid-cols-4">
                  <DhSpec label="State" value={<PcBadge value={g.status} labels={PC_CAL_LABEL} fills={PC_CAL_FILL} testId="pc-gauge-state" />} />
                  <DhSpec label="Valid through" value={cur ? `${pcDay(cur.dueOn)} · ${pcDueDaysPhrase(g.daysUntilDue)}` : '—'} />
                  <DhSpec label="Calibrated" value={pcDay(cur?.calibratedOn)} />
                  <DhSpec label="Range" mono value={cur ? range(cur) : '—'} />
                  <DhSpec label="Accuracy class" value={cur?.accuracyClass ?? '—'} />
                  <DhSpec label="Certificate" value={cur?.certificateNumber ?? '—'} />
                  <DhSpec label="Calibrated by" value={cur?.performedBy ?? '—'} />
                  <DhSpec label="Recorded" value={formatInstant(cur?.createdAt ?? null, tz) ?? '—'} />
                </dl>
              </DhPanel>
              <DhPanel
                eyebrow="Append-only · newest first"
                title="History"
                testId="pc-gauge-history"
                footer={g.history.length > 0 ? `${g.history.length} ${g.history.length === 1 ? 'calibration' : 'calibrations'}` : undefined}
              >
                {g.history.length === 0 ? (
                  <DhEmpty>This gauge has never been calibrated.{canRecord && ' Record one with Record calibration.'}</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr>
                          <th scope="col" className="px-4 py-3">Calibrated</th>
                          <th scope="col" className="px-4 py-3">Valid through</th>
                          <th scope="col" className="px-4 py-3">Range</th>
                          <th scope="col" className="px-4 py-3">Certificate</th>
                          <th scope="col" className="px-4 py-3">Calibrated by</th>
                          <th scope="col" className="px-4 py-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.history.map((c) => (
                          <tr key={c.id} className="border-t border-fieldiq-border" data-testid="pc-gauge-history-row">
                            <td className="px-4 py-3 text-sm text-white">{pcDay(c.calibratedOn)}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{pcDay(c.dueOn)}</td>
                            <td className="px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text">{range(c)}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{c.certificateNumber ?? '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{c.performedBy ?? '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{c.notes ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>
            </>
          ) : null}
        </div>
      </section>
      {canRecord && equipmentId && (
        <RecordCalibrationDialog open={recording} onClose={() => setRecording(false)} equipmentId={equipmentId} unit={unit} />
      )}
    </div>
  );
}
